import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";

const WordTranslationDialog = ({
    open,
    onClose,
    word,
    language,
    translationData,
    originalMeaning,
    loading,
    /** e.g. "korean" — key under meaningsI18n */
    targetLanguage = "korean",
    /** Full title for the translated side (e.g. "Korean translation" from the language pack) */
    targetColumnTitle
}) => {
    if (!open) return null;

    const languageName = language ? language.charAt(0).toUpperCase() + language.slice(1) : "";
    const translatedMeaning = translationData?.meaningsI18n?.[targetLanguage];
    const rightTitle =
        targetColumnTitle ||
        (targetLanguage ? `${String(targetLanguage).charAt(0).toUpperCase() + String(targetLanguage).slice(1).toLowerCase()} Translation` : "Translation");

    // Define order for parts of speech (noun first)
    const partOfSpeechOrder = ['nom', 'noun', 'verbe', 'verb', 'adjectif', 'adjective', 'adverbe', 'adverb', 'title'];
    
    // Helper function to sort entries by part of speech order
    const sortByPartOfSpeech = (entries) => {
        return entries.sort(([a], [b]) => {
            const indexA = partOfSpeechOrder.indexOf(a.toLowerCase());
            const indexB = partOfSpeechOrder.indexOf(b.toLowerCase());
            const aIndex = indexA === -1 ? 999 : indexA;
            const bIndex = indexB === -1 ? 999 : indexB;
            return aIndex - bIndex;
        });
    };

    // Helper function to format definition with proper line breaks
    const formatDefinition = (definition) => {
        if (typeof definition !== 'string') return String(definition);
        return definition.replace(/(\d+\.\s)/g, '\n$1').trim();
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
                }
            }}
        >
            <DialogTitle sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                pb: 1,
                borderBottom: '1px solid',
                borderColor: 'divider'
            }}>
                <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
                    {word} - {languageName} Translation
                </Typography>
            </DialogTitle>
            
            <DialogContent sx={{ pt: 3 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Grid container spacing={3}>
                        {/* Left side - Original Meaning */}
                        <Grid size={{ xs: 12, md: 6 }} sx={{ borderRight: { md: '1px solid' }, borderColor: { md: 'divider' }, pr: { md: 3 } }}>
                            <Typography 
                                variant="subtitle1" 
                                sx={{ 
                                    fontWeight: 'bold', 
                                    color: 'primary.main', 
                                    mb: 2,
                                    textTransform: 'capitalize'
                                }}
                            >
                                Original ({language})
                            </Typography>
                            {originalMeaning ? (
                                <Box>
                                    {sortByPartOfSpeech(Object.entries(originalMeaning))
                                        .filter(([key]) => key !== 'title')
                                        .map(([partOfSpeech, definition], index, array) => {
                                            const isLast = index === array.length - 1;

                                            return (
                                                <Box key={partOfSpeech} sx={{ mb: 3 }}>
                                                    <Typography 
                                                        variant="subtitle2" 
                                                        sx={{ 
                                                            fontWeight: 'bold', 
                                                            color: 'secondary.main', 
                                                            mb: 1.5,
                                                            textTransform: 'capitalize'
                                                        }}
                                                    >
                                                        {partOfSpeech}
                                                    </Typography>
                                                    
                                                    <Typography 
                                                        variant="body2" 
                                                        sx={{ 
                                                            whiteSpace: 'pre-line',
                                                            lineHeight: 1.8,
                                                            color: 'text.primary',
                                                            pl: 1
                                                        }}
                                                    >
                                                        {formatDefinition(definition)}
                                                    </Typography>
                                                    
                                                    {!isLast && (
                                                        <Divider sx={{ mt: 2, opacity: 0.3 }} />
                                                    )}
                                                </Box>
                                            );
                                        })}
                                </Box>
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    No original meaning available.
                                </Typography>
                            )}
                        </Grid>

                        {/* Right side - Korean Translation */}
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Typography 
                                variant="subtitle1" 
                                sx={{ 
                                    fontWeight: 'bold', 
                                    color: 'primary.main', 
                                    mb: 2,
                                    textTransform: 'capitalize'
                                }}
                            >
                                {rightTitle}
                            </Typography>
                            {translatedMeaning ? (
                                <Box>
                                    {sortByPartOfSpeech(Object.entries(translatedMeaning))
                                        .filter(([key]) => key !== 'title')
                                        .map(([partOfSpeech, definition], index, array) => {
                                            const isLast = index === array.length - 1;

                                            return (
                                                <Box key={partOfSpeech} sx={{ mb: 3 }}>
                                                    <Typography 
                                                        variant="subtitle2" 
                                                        sx={{ 
                                                            fontWeight: 'bold', 
                                                            color: 'secondary.main', 
                                                            mb: 1.5,
                                                            textTransform: 'capitalize'
                                                        }}
                                                    >
                                                        {partOfSpeech}
                                                    </Typography>
                                                    
                                                    <Typography 
                                                        variant="body2" 
                                                        sx={{ 
                                                            whiteSpace: 'pre-line',
                                                            lineHeight: 1.8,
                                                            color: 'text.primary',
                                                            pl: 1
                                                        }}
                                                    >
                                                        {formatDefinition(definition)}
                                                    </Typography>
                                                    
                                                    {!isLast && (
                                                        <Divider sx={{ mt: 2, opacity: 0.3 }} />
                                                    )}
                                                </Box>
                                            );
                                        })}
                                </Box>
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    No translation available.
                                </Typography>
                            )}
                        </Grid>
                    </Grid>
                )}
            </DialogContent>
            
            <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Button 
                    onClick={onClose}
                    variant="contained"
                    sx={{
                        backgroundColor: '#3BA666',
                        '&:hover': { backgroundColor: '#2e7d32' }
                    }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default WordTranslationDialog;

